import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Check, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DEFAULT_ITINERARY_SECTIONS, parseItinerarySections } from "@/lib/itinerarySections";

const CREATE = "__create_new_section__";
const NONE = "__auto_section__";

interface Props {
  id: string;
  leadId?: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  hint?: string;
}

/** Section picker with an inline "Create new section" option that saves to the lead. */
export function SectionSelectField({ id, leadId, value, onChange, options, hint }: Props) {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState("");
  const [extra, setExtra] = useState<string[]>([]);
  const all = [...new Set([...options, ...extra, ...(value.trim() ? [value.trim()] : [])])];

  const saveNew = async () => {
    const name = draft.trim().slice(0, 80);
    if (!name) return;
    const existing = all.find((s) => s.toLowerCase() === name.toLowerCase());
    const final = existing || name;
    setExtra((prev) => [...new Set([...prev, final])]);
    onChange(final);
    setCreating(false);
    setDraft("");
    if (!leadId || existing) return;
    const { data, error } = await supabase.from("leads").select("itinerary_sections").eq("id", leadId).single();
    if (error) return toast.error("Section added to this item, but couldn't save it to the programme list");
    const current = parseItinerarySections(data?.itinerary_sections);
    const base = current.length ? current : DEFAULT_ITINERARY_SECTIONS;
    if (base.some((s) => s.toLowerCase() === final.toLowerCase())) return;
    const { error: upErr } = await supabase.from("leads").update({ itinerary_sections: [...base, final] }).eq("id", leadId);
    if (upErr) return toast.error("Couldn't save the new section to the programme list");
    queryClient.invalidateQueries({ queryKey: ["lead-itinerary-layout", leadId] });
    queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
    toast.success(`Section "${final}" created`);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>Programme Section</Label>
      {creating ? (
        <div className="flex gap-2">
          <Input
            id={id}
            autoFocus
            value={draft}
            maxLength={80}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); saveNew(); }
              if (e.key === "Escape") { e.preventDefault(); setCreating(false); }
            }}
            placeholder="New section name, e.g. Cultural Evenings"
          />
          <Button type="button" size="icon" onClick={saveNew} disabled={!draft.trim()} aria-label="Save section">
            <Check className="h-4 w-4" />
          </Button>
          <Button type="button" size="icon" variant="outline" onClick={() => setCreating(false)} aria-label="Cancel">
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <Select
          value={value.trim() || NONE}
          onValueChange={(v) => {
            if (v === CREATE) { setCreating(true); return; }
            onChange(v === NONE ? "" : v);
          }}
        >
          <SelectTrigger id={id}><SelectValue placeholder="Choose a section" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Automatic (based on item type)</SelectItem>
            {all.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            <SelectSeparator />
            <SelectItem value={CREATE}>
              <span className="flex items-center gap-2 text-primary"><Plus className="h-4 w-4" />Create new section…</span>
            </SelectItem>
          </SelectContent>
        </Select>
      )}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
